import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronDown,
  faSearch,
  faTimes,
  faShieldAlt,
  faExternalLinkAlt,
  faSpinner,
  faFlask
} from '@fortawesome/free-solid-svg-icons';
import mitreService from '../../services/mitreService';
import atomicService from '../../services/atomicService';

// Tactic icons mapping
const tacticIcons = {
  'Reconnaissance': '🔍',
  'Resource Development': '🛠️',
  'Initial Access': '🚪',
  'Execution': '⚡',
  'Persistence': '📌',
  'Privilege Escalation': '⬆️',
  'Defense Evasion': '🛡️',
  'Credential Access': '🔑',
  'Discovery': '🔎',
  'Lateral Movement': '↔️',
  'Collection': '📦',
  'Command and Control': '📡',
  'Exfiltration': '📤',
  'Impact': '💥'
};

const MitreTechniqueSelector = ({
  onSelect,
  selectedTechnique,
  onClose,
  darkMode = false
}) => {
  const [techniqueTree, setTechniqueTree] = useState([]);
  const [atomicTests, setAtomicTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAtomicTests, setLoadingAtomicTests] = useState(true);
  const [expandedTactics, setExpandedTactics] = useState({});
  const [expandedTechniques, setExpandedTechniques] = useState({});
  const [expandedAtomicTests, setExpandedAtomicTests] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTechniqueData, setSelectedTechniqueData] = useState(null);
  const [selectedAtomicTest, setSelectedAtomicTest] = useState(null);
  const [visibleTechniquesPerTactic, setVisibleTechniquesPerTactic] = useState({});
  const INITIAL_VISIBLE = 20; // Show 20 techniques initially per tactic

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setLoadingAtomicTests(true);

      // Fetch both MITRE techniques and atomic tests in parallel
      const [tree, atomicData] = await Promise.all([
        mitreService.getTechniqueTree(),
        atomicService.getAtomicTests({ limit: 5000 })
      ]);

      setTechniqueTree(tree);

      // Handle atomic tests data structure
      const testsArray = Array.isArray(atomicData) ? atomicData : (atomicData?.tests || []);
      setAtomicTests(testsArray);

      // Don't auto-expand anything for better performance
      // User can expand what they need
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
      setLoadingAtomicTests(false);
    }
  };

  // Filter techniques based on search and enrich with atomic tests
  const filteredTree = useMemo(() => {
    const enrichWithAtomicTests = (techniques) => {
      return techniques.map(tech => {
        // Find all atomic tests for this technique
        const techniqueAtomicTests = atomicTests.filter(
          test => test.techniqueId === tech.id
        );

        // Calculate total test count
        const totalTestCount = techniqueAtomicTests.reduce(
          (sum, t) => sum + (t.atomicTests?.length || 0),
          0
        );

        return {
          ...tech,
          atomicTests: techniqueAtomicTests,
          atomicTestCount: totalTestCount,
          subTechniques: tech.subTechniques?.map(sub => {
            const subAtomicTests = atomicTests.filter(
              test => test.techniqueId === sub.id
            );
            const subTestCount = subAtomicTests.reduce(
              (sum, t) => sum + (t.atomicTests?.length || 0),
              0
            );
            return {
              ...sub,
              atomicTests: subAtomicTests,
              atomicTestCount: subTestCount
            };
          })
        };
      });
    };

    const tree = searchTerm.trim() ? techniqueTree : techniqueTree;
    const term = searchTerm.toLowerCase();

    return tree.map(tactic => {
      let filteredTechniques = tactic.techniques;

      // Apply search filter if needed
      if (searchTerm.trim()) {
        filteredTechniques = filteredTechniques.filter(tech => {
          const matchesTech =
            tech.id.toLowerCase().includes(term) ||
            tech.name.toLowerCase().includes(term) ||
            (tech.description && tech.description.toLowerCase().includes(term));

          const matchesSub = tech.subTechniques?.some(sub =>
            sub.id.toLowerCase().includes(term) ||
            sub.name.toLowerCase().includes(term) ||
            (sub.description && sub.description.toLowerCase().includes(term))
          );

          return matchesTech || matchesSub;
        }).map(tech => ({
          ...tech,
          subTechniques: tech.subTechniques?.filter(sub =>
            sub.id.toLowerCase().includes(term) ||
            sub.name.toLowerCase().includes(term) ||
            (sub.description && sub.description.toLowerCase().includes(term)) ||
            tech.id.toLowerCase().includes(term) ||
            tech.name.toLowerCase().includes(term)
          )
        }));
      }

      // Enrich with atomic tests
      const enrichedTechniques = enrichWithAtomicTests(filteredTechniques);

      return {
        ...tactic,
        techniques: enrichedTechniques,
        count: enrichedTechniques.length
      };
    }).filter(tactic => tactic.techniques.length > 0);
  }, [techniqueTree, atomicTests, searchTerm]);

  // Auto-expand all when searching
  useEffect(() => {
    if (searchTerm.trim()) {
      const allTactics = {};
      const allTechniques = {};
      filteredTree.forEach(tactic => {
        allTactics[tactic.name] = true;
        tactic.techniques.forEach(tech => {
          if (tech.subTechniques?.length > 0) {
            allTechniques[tech.id] = true;
          }
        });
      });
      setExpandedTactics(allTactics);
      setExpandedTechniques(allTechniques);
    }
  }, [searchTerm, filteredTree]);

  const toggleTactic = (tacticName) => {
    setExpandedTactics(prev => ({
      ...prev,
      [tacticName]: !prev[tacticName]
    }));
    // Initialize visible count when expanding
    if (!expandedTactics[tacticName] && !visibleTechniquesPerTactic[tacticName]) {
      setVisibleTechniquesPerTactic(prev => ({
        ...prev,
        [tacticName]: INITIAL_VISIBLE
      }));
    }
  };

  const showMoreTechniques = (tacticName) => {
    setVisibleTechniquesPerTactic(prev => ({
      ...prev,
      [tacticName]: (prev[tacticName] || INITIAL_VISIBLE) + 20
    }));
  };

  const toggleTechnique = (techId) => {
    setExpandedTechniques(prev => ({
      ...prev,
      [techId]: !prev[techId]
    }));
  };

  // Store the tactic for the selected technique
  const [selectedTactic, setSelectedTactic] = useState(null);

  // Preview technique details (don't confirm selection yet)
  const handlePreviewTechnique = (technique, tactic) => {
    setSelectedTechniqueData(technique);
    setSelectedTactic(tactic);
  };

  // Toggle atomic tests panel for a technique
  const toggleAtomicTests = (techId) => {
    setExpandedAtomicTests(prev => ({
      ...prev,
      [techId]: !prev[techId]
    }));
  };

  // Select an atomic test
  const handleSelectAtomicTest = (atomicTest, individualTest, technique) => {
    setSelectedAtomicTest({
      atomicTest,
      individualTest,
      technique
    });
    setSelectedTechniqueData(technique);
  };

  // Confirm selection - either technique only or technique + atomic test
  const handleConfirmSelection = () => {
    if (selectedAtomicTest) {
      // User selected a specific atomic test
      const { atomicTest, individualTest, technique } = selectedAtomicTest;
      onSelect({
        // Technique data
        id: technique.id,
        name: technique.name,
        description: technique.description,
        tactic: selectedTactic.name,
        url: technique.url,
        // Atomic test data
        atomicTest: {
          atomicTest,
          individualTest,
          technique,
          fullCommand: individualTest.executor?.command,
          inputArguments: individualTest.inputArguments || []
        }
      });
    } else if (selectedTechniqueData && selectedTactic) {
      // User selected just the technique
      onSelect({
        id: selectedTechniqueData.id,
        name: selectedTechniqueData.name,
        description: selectedTechniqueData.description,
        tactic: selectedTactic.name,
        url: selectedTechniqueData.url
      });
    }
  };

  const baseClasses = darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900';
  const borderClasses = darkMode ? 'border-gray-700' : 'border-gray-200';
  const hoverClasses = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100';
  const selectedClasses = darkMode ? 'bg-blue-900 border-blue-500' : 'bg-blue-50 border-blue-500';

  if (loading) {
    return (
      <div className={`p-8 text-center ${baseClasses}`}>
        <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-blue-500 mb-2" />
        <p>Loading MITRE ATT&CK framework...</p>
      </div>
    );
  }

  return (
    <div className={`flex h-[70vh] ${baseClasses} rounded-lg overflow-hidden border ${borderClasses}`}>
      {/* Left Panel - Technique Tree */}
      <div className={`w-1/2 border-r ${borderClasses} flex flex-col`}>
        {/* Search */}
        <div className={`p-3 border-b ${borderClasses}`}>
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search techniques..."
              className={`w-full pl-10 pr-8 py-2 rounded-lg border ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 placeholder-gray-500'
              }`}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        </div>

        {/* Technique Tree */}
        <div className="flex-1 overflow-y-auto">
          {filteredTree.length === 0 ? (
            <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No techniques found matching "{searchTerm}"
            </div>
          ) : (
            filteredTree.map((tactic) => (
              <div key={tactic.name} className="border-b border-gray-200 dark:border-gray-700">
                {/* Tactic Header */}
                <button
                  onClick={() => toggleTactic(tactic.name)}
                  className={`w-full flex items-center justify-between p-3 ${hoverClasses} transition-colors`}
                >
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={expandedTactics[tactic.name] ? faChevronDown : faChevronRight}
                      className={`w-4 mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                    />
                    <span className="mr-2">{tacticIcons[tactic.name] || '📁'}</span>
                    <span className="font-medium">{tactic.name}</span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {tactic.count}
                  </span>
                </button>

                {/* Techniques */}
                {expandedTactics[tactic.name] && (
                  <div className="pl-4">
                    {tactic.techniques.slice(0, searchTerm ? 9999 : (visibleTechniquesPerTactic[tactic.name] || INITIAL_VISIBLE)).map((tech) => (
                      <div key={tech.id}>
                        {/* Parent Technique */}
                        <div className={`flex items-center`}>
                          {tech.subTechniques?.length > 0 && (
                            <button
                              onClick={() => toggleTechnique(tech.id)}
                              className={`p-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                              <FontAwesomeIcon
                                icon={expandedTechniques[tech.id] ? faChevronDown : faChevronRight}
                                className="w-3"
                              />
                            </button>
                          )}
                          <button
                            onClick={() => handlePreviewTechnique(tech, tactic)}
                            className={`flex-1 flex items-center p-2 text-left rounded ${
                              selectedTechniqueData?.id === tech.id
                                ? selectedClasses
                                : hoverClasses
                            } transition-colors`}
                          >
                            <span className={`font-mono text-xs mr-2 px-1.5 py-0.5 rounded ${
                              darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'
                            }`}>
                              {tech.id}
                            </span>
                            <span className={`text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                              {tech.name}
                            </span>
                            <div className="ml-auto flex items-center gap-1">
                              {tech.atomicTestCount > 0 && (
                                <span className={`text-xs px-1.5 py-0.5 rounded-full flex items-center gap-1 ${
                                  darkMode ? 'bg-orange-900/40 text-orange-400' : 'bg-orange-100 text-orange-700'
                                }`}>
                                  <FontAwesomeIcon icon={faFlask} className="text-xs" />
                                  {tech.atomicTestCount}
                                </span>
                              )}
                              {tech.subTechniques?.length > 0 && (
                                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  +{tech.subTechniques.length}
                                </span>
                              )}
                            </div>
                          </button>
                        </div>

                        {/* Sub-techniques */}
                        {expandedTechniques[tech.id] && tech.subTechniques?.length > 0 && (
                          <div className="pl-8 border-l-2 border-gray-300 dark:border-gray-600 ml-6 mb-2">
                            {tech.subTechniques.map((subTech) => (
                              <button
                                key={subTech.id}
                                onClick={() => handlePreviewTechnique(subTech, tactic)}
                                className={`w-full flex items-center p-2 text-left rounded ${
                                  selectedTechniqueData?.id === subTech.id
                                    ? selectedClasses
                                    : hoverClasses
                                } transition-colors`}
                              >
                                <span className={`font-mono text-xs mr-2 px-1.5 py-0.5 rounded ${
                                  darkMode ? 'bg-orange-900 text-orange-200' : 'bg-orange-100 text-orange-700'
                                }`}>
                                  {subTech.id}
                                </span>
                                <span className={`text-sm truncate flex-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                  {subTech.name}
                                </span>
                                {subTech.atomicTestCount > 0 && (
                                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full flex items-center gap-1 ${
                                    darkMode ? 'bg-orange-900/40 text-orange-400' : 'bg-orange-100 text-orange-700'
                                  }`}>
                                    <FontAwesomeIcon icon={faFlask} className="text-xs" />
                                    {subTech.atomicTestCount}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {/* Show More Button */}
                    {!searchTerm && tactic.techniques.length > (visibleTechniquesPerTactic[tactic.name] || INITIAL_VISIBLE) && (
                      <button
                        onClick={() => showMoreTechniques(tactic.name)}
                        className={`w-full p-2 text-center text-sm ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} font-medium`}
                      >
                        Show {Math.min(20, tactic.techniques.length - (visibleTechniquesPerTactic[tactic.name] || INITIAL_VISIBLE))} more techniques...
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Technique Details */}
      <div className={`w-1/2 flex flex-col`}>
        {selectedTechniqueData ? (
          <>
            <div className={`p-4 border-b ${borderClasses}`}>
              <div className="flex items-start justify-between">
                <div>
                  <span className={`font-mono text-sm px-2 py-1 rounded mr-2 ${
                    darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedTechniqueData.id}
                  </span>
                  <h3 className={`text-lg font-bold mt-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedTechniqueData.name}
                  </h3>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {selectedTechniqueData.tactic}
                  </p>
                </div>
                {selectedTechniqueData.url && (
                  <a
                    href={selectedTechniqueData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-600"
                    title="View on MITRE ATT&CK"
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </a>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Description
              </h4>
              <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {selectedTechniqueData.description || 'No description available.'}
              </p>

              {selectedTechniqueData.platforms?.length > 0 && (
                <div className="mt-4">
                  <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Platforms
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedTechniqueData.platforms.map((platform, idx) => (
                      <span
                        key={idx}
                        className={`px-2 py-1 text-xs rounded ${
                          darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Atomic Tests Section */}
              {selectedTechniqueData.atomicTestCount > 0 && (
                <div className="mt-4">
                  <button
                    onClick={() => toggleAtomicTests(selectedTechniqueData.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      expandedAtomicTests[selectedTechniqueData.id]
                        ? (darkMode ? 'bg-orange-900/30 border-orange-700' : 'bg-orange-50 border-orange-200')
                        : (darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200')
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faFlask} className="text-orange-500" />
                      <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        Available Atomic Tests ({selectedTechniqueData.atomicTestCount})
                      </span>
                    </div>
                    <FontAwesomeIcon
                      icon={expandedAtomicTests[selectedTechniqueData.id] ? faChevronDown : faChevronRight}
                      className={darkMode ? 'text-gray-400' : 'text-gray-500'}
                    />
                  </button>

                  {expandedAtomicTests[selectedTechniqueData.id] && (
                    <div className="mt-2 space-y-2">
                      {selectedTechniqueData.atomicTests?.map((atomicTest) => (
                        <div key={atomicTest._id}>
                          {atomicTest.atomicTests?.map((test, index) => (
                            <div
                              key={index}
                              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                selectedAtomicTest?.individualTest === test
                                  ? (darkMode ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-500')
                                  : (darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-gray-100')
                              }`}
                              onClick={() => handleSelectAtomicTest(atomicTest, test, selectedTechniqueData)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h5 className={`font-medium text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                    {test.name}
                                  </h5>
                                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {test.description}
                                  </p>
                                  <div className="flex items-center gap-2 mt-2">
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                                    }`}>
                                      {test.executor?.type || 'manual'}
                                    </span>
                                    {test.executor?.elevationRequired && (
                                      <span className="text-xs text-orange-500">⚠ Elevated</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={`p-4 border-t ${borderClasses}`}>
              {selectedAtomicTest ? (
                <button
                  onClick={handleConfirmSelection}
                  className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faFlask} />
                  Select Test: {selectedAtomicTest.individualTest.name}
                </button>
              ) : (
                <button
                  onClick={handleConfirmSelection}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Select {selectedTechniqueData.id} - {selectedTechniqueData.name}
                </button>
              )}
            </div>
          </>
        ) : (
          <div className={`flex-1 flex items-center justify-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <div className="text-center">
              <FontAwesomeIcon icon={faShieldAlt} className="text-5xl mb-4 opacity-50" />
              <p>Select a technique from the tree</p>
              <p className="text-sm mt-2">to see its description and details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MitreTechniqueSelector;
